import isEmpty from "lodash/isEmpty";
import { useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { GuestQuestionProps } from "./GuestQuestion.props";

// react-hook-form is supported by React Native, so form logic can be used in this shared hook
export function useGuestQuestion(
  props: GuestQuestionProps & { form: UseFormReturn }
) {
  const { form, formId, questionIndex, options, required, onUpdateQuestion } =
    props;

  const [hasCheckboxSelection, setHasCheckboxSelection] = useState(false);
  const updateHasCheckboxSelection = (val: boolean) => {
    setHasCheckboxSelection(val);
  };

  // id for the current form input
  const inputId = `${formId}-${questionIndex}`;

  // options for select dropdown questions
  const selectOptions = useMemo(
    () =>
      options?.map((opt: { text: string }, index: number) => {
        return { id: index, name: opt.text };
      }),
    [options]
  );

  // updates the current selection for questions with dropdown selectors
  const updateDropdownSelection = (response: string | null | undefined) => {
    const formattedResponse = !isEmpty(response) ? response : null;
    form.setValue(inputId, formattedResponse);
    onUpdateQuestion(inputId);
  };

  // handles validation for the entire checkbox group
  // (if the checkbox question is required, at least 1 option must be selected)
  const updateCheckboxGroupValidation = () => {
    const formValues = form.getValues(inputId) ?? {};
    const hasSelections = !isEmpty(formValues);
    updateHasCheckboxSelection(hasSelections);
  };

  // if the checkbox array is now empty (nothing is selected),
  // reset the form value to null (default empty value)
  const resetCheckboxGroup = () => {
    const newFormValues = form.getValues(inputId) ?? {};
    const noSelections = isEmpty(newFormValues);
    if (noSelections) {
      form.setValue(inputId, null);
    }
  };

  // for checkbox group questions, handles all logic related to selecting/deselecting checkboxes
  const updateCheckboxSelection = (
    selectedOption: string,
    isChecked: boolean
  ) => {
    const formValues: { [x: string]: boolean } = form.getValues(inputId) ?? {};

    // adds or removes the individual checkbox selection to the form object
    if (isChecked) {
      form.setValue(inputId, { ...formValues, [selectedOption]: isChecked });
    } else {
      delete formValues[selectedOption];
    }

    // updates the entire checkbox group's validation status
    if (required) {
      updateCheckboxGroupValidation();
    }

    // resets the form value from {} to null if the checkbox selections are now empty
    resetCheckboxGroup();

    // updates the basket's question object with the current checkbox selections
    onUpdateQuestion(inputId);
  };

  return {
    inputId,
    selectOptions,
    hasCheckboxSelection,
    updateCheckboxSelection,
    updateDropdownSelection,
  };
}
